
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, Auth, onIdTokenChanged } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';

// Configuration Note: In a production environment, these should be environment variables.
// For the purpose of this single-file structure app, we ensure the process.env placeholders are ready.
// You must replace these values with your actual Firebase Project config or set them in your bundler.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

let app;
let auth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;
let googleProvider: GoogleAuthProvider | null = null;

try {
  // Only initialize if we have a valid key, otherwise fallback to null (simulated mode)
  if (firebaseConfig.apiKey && firebaseConfig.apiKey !== "undefined") {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app, 'auratraveler-firebase');

    if (firebaseConfig.storageBucket) {
        storage = getStorage(app);
    } else {
        console.warn("Firebase Storage Bucket missing. Images will fallback to placeholders.");
    }

    googleProvider = new GoogleAuthProvider();
  } else {
    console.warn("Firebase Config missing or invalid. App running in Simulation Mode.");
  }
} catch (e) {
  console.warn("Firebase Initialization Error:", e);
}

// --- AUTH TOKEN REFRESH HELPERS ---

// Track the last token refresh time to detect expiry windows
let lastTokenRefreshTime: number = Date.now();
const TOKEN_EXPIRY_MS = 60 * 60 * 1000; // 60 minutes (Firebase default)
const TOKEN_WARNING_THRESHOLD_MS = 55 * 60 * 1000; // Warn at 55 minutes

// Listen for token changes to keep track of refresh times
if (auth) {
  onIdTokenChanged(auth, (user) => {
    if (user) {
      lastTokenRefreshTime = Date.now();
    }
  });
}

/**
 * Ensures the current user is authenticated and has a valid token.
 * Call this before any Firestore write operation.
 * Returns the ID token string on success, or null if the user is not authenticated.
 */
export const ensureAuthenticated = async (): Promise<string | null> => {
  if (!auth) return null; // Simulation mode

  const user = auth.currentUser;
  if (!user) return null;

  try {
    // First try without force refresh (uses cached token if still valid)
    const token = await user.getIdToken(false);
    return token;
  } catch (error) {
    console.error('[Auth] Token refresh failed:', error);
    // Try force refresh as fallback
    try {
      const token = await user.getIdToken(true);
      return token;
    } catch (refreshError) {
      console.error('[Auth] Force refresh failed:', refreshError);
      return null;
    }
  }
};

/**
 * Returns the time in milliseconds since the last token refresh.
 * Used to determine if a session timeout warning should be shown.
 */
export const getTimeSinceLastRefresh = (): number => {
  return Date.now() - lastTokenRefreshTime;
};

/**
 * Returns true if the token is approaching expiry (within 5 minutes of the 60-minute window).
 */
export const isTokenNearExpiry = (): boolean => {
  return getTimeSinceLastRefresh() >= TOKEN_WARNING_THRESHOLD_MS;
};

/**
 * Force-refreshes the auth token. Call this when the user clicks
 * the "stay logged in" prompt or before a critical operation.
 */
export const forceTokenRefresh = async (): Promise<boolean> => {
  if (!auth?.currentUser) return false;
  try {
    await auth.currentUser.getIdToken(true);
    lastTokenRefreshTime = Date.now();
    return true;
  } catch (error) {
    console.error('[Auth] Force token refresh failed:', error);
    return false;
  }
};

// --- LOCAL STORAGE BACKUP FOR UNSAVED TRIPS ---
const UNSAVED_TRIP_KEY = 'aura_unsaved_trip_backup';

export interface UnsavedTripBackup {
  tripData: any;
  userData: any;
  headerImage?: string;
  savedAt: number;
  userId: string;
}

/**
 * Saves trip data to localStorage as a backup in case auth fails during generation.
 */
export const backupTripToLocalStorage = (data: UnsavedTripBackup): void => {
  try {
    localStorage.setItem(UNSAVED_TRIP_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('[Auth] Failed to backup trip to localStorage:', e);
  }
};

/**
 * Retrieves any unsaved trip backup from localStorage.
 */
export const getUnsavedTripBackup = (): UnsavedTripBackup | null => {
  try {
    const raw = localStorage.getItem(UNSAVED_TRIP_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UnsavedTripBackup;
  } catch (e) {
    console.error('[Auth] Failed to read trip backup from localStorage:', e);
    return null;
  }
};

/**
 * Clears the unsaved trip backup from localStorage (call after successful save).
 */
export const clearTripBackup = (): void => {
  try {
    localStorage.removeItem(UNSAVED_TRIP_KEY);
  } catch (e) {
    // Non-critical, just log
    console.error('[Auth] Failed to clear trip backup:', e);
  }
};

export { auth, db, storage, googleProvider };
